import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
// components
import { DetailsButton } from 'components/UI'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// views
import { DetailsButtonCSS } from 'components/CTAs/PlanObjectiveActionsMenu'
// constants
import { Modals } from 'constants/index'
// icons
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
import BookmarkIcon from '@mui/icons-material/BookmarkBorderOutlined'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
// context
import { ActionContext } from 'context/index'
import { ActionsPageContext } from 'containers/Actions/Register/layers/API'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// graphql
import { PLAN_ACTION_DELETE_MUTATION } from 'graphql/planActions/mutations'
import { PLAN_ITEM_SET_ARCHIVED_MUTATION } from 'graphql/plan/mutations'
// types
import { ISecurityProfilePermissions } from 'types/graphql'
// hooks
import { useSnackbar } from 'notistack'
import { useBookmarks, useShowModal } from 'hooks'
// utils
import get from 'lodash/get'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface Props {
  hideParent: () => void
}

const PlanActionActionsMenu: React.FC<Props> = ({ hideParent }) => {
  const router = useRouter()

  const { entity, refetch } = React.useContext<any>(ActionContext)
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { refetch: planActionsListRefetch } = React.useContext(ActionsPageContext)

  const { actionDeleteAction, actionArchiveAction } = entity.currentPermissions as ISecurityProfilePermissions

  const [deleteMutation] = useMutation(PLAN_ACTION_DELETE_MUTATION)
  const [archiveMutation] = useMutation(PLAN_ITEM_SET_ARCHIVED_MUTATION)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_ACTION_MODAL })

  const shouldArchive = !entity.archived && actionArchiveAction

  const createOrDeleteBookmark = useBookmarks()

  const handleDeleteClick = () => {
    if (get(entity, 'planItems.data').length > 1) {
      return enqueueSnackbar('Record can not be deleted as it is referenced by another record!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    const planItemId = get(entity, 'planItems.data[0].id')

    showModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          if (shouldArchive) {
            await archiveMutation({ variables: { id: planItemId, archived: true } })
          } else {
            await deleteMutation({ variables: { id: entity.id } })
          }

          await treeQueryRefetch?.()
          await planActionsListRefetch?.()

          hideParent()
        } catch (errors) {
          console.log('[PlanActionActionsMenu.handleDeleteClick]: ', parseGraphQLErrorMessages(errors))
        }
      }
    })
  }

  const onCreateOrDeleteBookmark = async () => {
    const input = {
      planActionId: entity.id
    }

    await createOrDeleteBookmark(input, entity.isBookmark)

    refetch()

    if (router.query.slug?.[0] === 'bookmarks') {
      planActionsListRefetch?.()
    }
  }

  const handleUnarchiveClick = async () => {
    const planItemId = get(entity, 'planItems.data[0].id')

    try {
      await archiveMutation({ variables: { id: planItemId, archived: false } })
      await treeQueryRefetch?.()
    } catch (errors) {
      console.log('[PlanActionActionsMenu.handleUnarchiveClick]: ', parseGraphQLErrorMessages(errors))
    }
  }

  return (
    <>
      <DetailsButton styles={DetailsButtonCSS}>
        <span onClick={onCreateOrDeleteBookmark}>
          <BookmarkIcon />
          {entity.isBookmark ? 'Remove from bookmarks' : 'Add to bookmarks'}
        </span>
        {actionArchiveAction && entity.archived && (
          <span onClick={handleUnarchiveClick}>
            <UnarchiveIcon />
            Unarchive
          </span>
        )}
        {(actionDeleteAction || actionArchiveAction) && (
          <span onClick={handleDeleteClick}>
            <DeleteIcon />
            {shouldArchive ? 'Archive' : 'Delete'}
          </span>
        )}
      </DetailsButton>
      <ConfirmDeleteModal
        header={shouldArchive ? `Archive ${entity.description}` : `Delete ${entity.description}`}
        modalType={Modals.CONFIRM_DELETE_ACTION_MODAL}>
        {shouldArchive ? (
          'Are you sure you want to archive this plan action? This will also archive any sub-items.'
        ) : (
          <span>
            Are you sure you want to delete this plan action? This will also <b>delete</b> any sub-items.
          </span>
        )}
      </ConfirmDeleteModal>
    </>
  )
}

export default PlanActionActionsMenu
